import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import { firestore, COMM_COLL } from "_firebase";
import { selectCommUpgradePackages } from "../Upgrades/_ducks/selectors";
import { selectFuelCosts } from "store/community/selectors";

import TabHeader from "components/TabHeader";
import UpperGraph from "./Graphs/UpperGraph";
import BottomGraph from "./Graphs/BottomGraph";
import AnalysisIsRunning from "./AnalysisIsRunning";
import NoAnalysisWereRun from "./NoAnalysisWereRun";
import AggregatedData from "./AggregatedData";

import { getAllModelsInTheRun, checkIsSimulationRunning, getAllUpgradePackagesInTheRun } from "utils/communities";

import {
    selectCommunityId,
    selectCommunityRunHistory,
    selectCommunityModelData,
    selectModelUpgradesRun,
    selectCommunityModelDir,
    selectCommunityEcmInputMap,
} from "store/community/selectors";
import {
    fetchCommModelDetails,
    fetchCommunityRunHistory,
    watchRunResultsUpdates,
    fetchCommunityRunResultsByMeasure,
} from "store/community/thunk";

import classes from "./styles.module.scss";

const Results = ({
    communityId,
    commModelDir = {},
    fetchCommunityRunHistory,
    runHistory = {},
    fetchCommunityRunResultsByMeasure,
    modelsData,
    modelUpgradesRun,
    commUpgradePackages,
    fuelPrices = {},
    ecmInputMap = {},
}) => {
    const [runData, setRunData] = useState(null);
    const [allUpgradePackages, setAllUpgradePackages] = useState([]);
    const [allModels, setAllModels] = useState([]);
    const [upperChartWidth, setUpperChartWidth] = useState(0);
    const [applyModelResultMultiplier, setApplyModelResultMultiplier] = useState(true);

    const scrollContainerRef = React.useRef(null);
    const bottomScrollbarRef = React.useRef(null);

    const communityModelCountsMap = Object.keys(commModelDir?.models || {}).reduce((acc, modelId) => {
        const { countDirection: { copyDirections = {} } = {} } = commModelDir?.models?.[modelId] || {};

        return {
            ...acc,
            ...Object.keys(copyDirections).reduce((acc, direction) => {
                if (copyDirections[direction] > 0) {
                    return {
                        ...acc,
                        [`${modelId}_${direction}`]: copyDirections[direction],
                    };
                }
                return acc;
            }, {}),
        };
    }, {});

    // Bottom Graph

    // Loading state
    const [isLoading, setIsLoading] = useState(true);

    // Only initialize selected models once when run data first loads; otherwise Firestore
    // snapshot callbacks can overwrite the user's selection when both filters are present.
    const modelsInitializedRef = useRef(false);

    useEffect(() => {
        if (!communityId) return;

        modelsInitializedRef.current = false;

        const historyRef = firestore.collection(`${COMM_COLL}/${communityId}/runHistory`);
        const resultsRef = firestore.collection(`${COMM_COLL}/${communityId}/runResults`);
        const resultsByMeasureRef = firestore.collection(`${COMM_COLL}/${communityId}/runResultsByMeasure`);

        let currentHistory = {};
        let currentResults = {};
        let currentResultsByMeasure = {};

        const updateUnifiedState = () => {
            const runData = {
                batchIds: Object.keys(currentHistory),
                runHistory: currentHistory,
                runResults: currentResults,
                runResultsByMeasure: currentResultsByMeasure,
            };

            setRunData(runData);

            const allModels = getAllModelsInTheRun(runData);
            setAllModels(allModels);
        };

        const unsubHistory = historyRef.onSnapshot((snapshot) => {
            currentHistory = Object.fromEntries(snapshot.docs.map((doc) => [doc.id, doc.data()]));
            updateUnifiedState();
        });

        const unsubResults = resultsRef.onSnapshot((snapshot) => {
            currentResults = Object.fromEntries(snapshot.docs.map((doc) => [doc.id, doc.data()]));
            updateUnifiedState();
        });

        const unsubResultsByMeasure = resultsByMeasureRef.onSnapshot((snapshot) => {
            currentResultsByMeasure = snapshot.docs.map((doc) => [doc.data()]).flat();
            updateUnifiedState();
        });

        return () => {
            unsubHistory();
            unsubResults();
        };
    }, [communityId]);

    useEffect(() => {
        if (!communityId) {
            return;
        }

        (async () => {
            setIsLoading(true);
            const runData = await fetchCommunityRunHistory(communityId);
            const runResultsByMeasure = await fetchCommunityRunResultsByMeasure(communityId);

            setRunData({ ...runData, runResultsByMeasure });

            const allModels = getAllModelsInTheRun(runData);
            const allUpgradePackages = getAllUpgradePackagesInTheRun(
                runData,
                modelUpgradesRun,
                allModels.map(({ value }) => value),
                commUpgradePackages,
                true
            );

            setAllModels(allModels);
            setAllUpgradePackages(allUpgradePackages);
            setIsLoading(false);
        })();
    }, [communityId]);

    const isSimulationRunning = checkIsSimulationRunning(runHistory);

    // Explicit callback that only ever updates bottom-graph state (avoids mix-up with upper-graph setter)

    return (
        <div className={classes.resultsContainer}>
            <TabHeader
                className={classes.resultsHeader}
                title="Results"
                description="View the results of your community simulations."
                additionalContentClassName={
                    isSimulationRunning ? classes.additionalContentLoading : classes.resultsAdditionalContent
                }
                noBorder={true}
            />
            {(isLoading || isSimulationRunning) && (
                <AnalysisIsRunning
                    loadingText={isLoading ? "Loading analysis..." : "Analysis is running..."}
                />
            )}
            {!isSimulationRunning && !isLoading && isEmpty(runHistory) && <NoAnalysisWereRun />}
            {!isSimulationRunning && !isLoading && !isEmpty(runHistory) && !isEmpty(runData?.runResults) && (
                <UpperGraph
                    runData={runData}
                    allModels={allModels}
                    communityModelCountsMap={communityModelCountsMap}
                    allPackages={commUpgradePackages}
                    ecmInputMap={ecmInputMap}
                    scrollContainerRef={scrollContainerRef}
                    bottomScrollbarRef={bottomScrollbarRef}
                    onChartWidthChange={setUpperChartWidth}
                    fuelPrices={fuelPrices}
                    applyModelResultMultiplier={applyModelResultMultiplier}
                    setApplyModelResultMultiplier={setApplyModelResultMultiplier}
                />
            )}
            {!isSimulationRunning && !isLoading && !isEmpty(runHistory) && !isEmpty(runData?.runResults) && (
                <BottomGraph
                    allModels={allModels}
                    allUpgradePackages={allUpgradePackages}
                    runData={runData}
                    modelUpgradesRun={modelUpgradesRun}
                    fuelPrices={fuelPrices}
                    communityModelCountsMap={communityModelCountsMap}
                    applyModelResultMultiplier={applyModelResultMultiplier}
                />
            )}
            {!isSimulationRunning && !isLoading && !isEmpty(runHistory) && !isEmpty(runData?.runResults) && (
                <AggregatedData
                    allModels={allModels}
                    modelsData={modelsData}
                    communityModelCountsMap={communityModelCountsMap}
                    applyModelResultMultiplier={applyModelResultMultiplier}
                    setApplyModelResultMultiplier={setApplyModelResultMultiplier}
                />
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    communityId: selectCommunityId,
    runHistory: selectCommunityRunHistory,
    modelsData: selectCommunityModelData,
    modelUpgradesRun: selectModelUpgradesRun,
    commUpgradePackages: selectCommUpgradePackages,
    fuelPrices: selectFuelCosts,
    ecmInputMap: selectCommunityEcmInputMap,
});

const mapDispatchToProps = (dispatch, { history }) => ({
    fetchCommModelDetails: async (communityId, modelId) => {
        return dispatch(fetchCommModelDetails(communityId, modelId));
    },
    fetchCommunityRunHistory: async (communityId) => {
        return dispatch(fetchCommunityRunHistory(communityId));
    },
    watchRunResultsUpdates: (communityId) => dispatch(watchRunResultsUpdates(communityId)),
    fetchCommunityRunResultsByMeasure: async (communityId) => dispatch(fetchCommunityRunResultsByMeasure(communityId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Results);
